'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import AdminKPIGrid from './AdminKPIGrid';
import AdminQRTypeChart from './AdminQRTypeChart';
import AdminTopDomains from './AdminTopDomains';
import AdminActivityTable from './AdminActivityTable';

// Mock admin stats — Backend integration point: GET /api/admin/stats
export const adminStats = {
  totalQRGenerated: 1847,
  todayQRGenerated: 34,
  totalDownloads: 6293,
  activeUsers: 412,
  todayChange: +12,
  totalChange: +8.4,
  downloadsChange: +15.2,
  usersChange: +3.1,
};

export const qrTypeDistribution = [
  { type: 'URL', count: 824, pct: 44.6 },
  { type: 'UPI', count: 312, pct: 16.9 },
  { type: 'WhatsApp', count: 287, pct: 15.5 },
  { type: 'WiFi', count: 156, pct: 8.4 },
  { type: 'Email', count: 112, pct: 6.1 },
  { type: 'Phone', count: 88, pct: 4.8 },
  { type: 'Maps', count: 44, pct: 2.4 },
  { type: 'Text', count: 24, pct: 1.3 },
];

export const topDomains = [
  { domain: 'knowourindia.com', count: 234, pct: 28.4 },
  { domain: 'novamaxstudios.com', count: 187, pct: 22.7 },
  { domain: 'razorpay.com', count: 98, pct: 11.9 },
  { domain: 'instagram.com', count: 76, pct: 9.2 },
  { domain: 'linktr.ee', count: 64, pct: 7.8 },
  { domain: 'youtube.com', count: 52, pct: 6.3 },
  { domain: 'maps.google.com', count: 44, pct: 5.3 },
  { domain: 'wa.me', count: 69, pct: 8.4 },
];

export const recentActivity = [
  { id: 'act-001', type: 'url', content: 'https://knowourindia.com/rajasthan', ip: '103.21.xx.xx', location: 'Mumbai, IN', createdAt: '2026-08-03T12:28:00Z', downloadCount: 2 },
  { id: 'act-002', type: 'upi', content: 'upi://pay?pa=merchant@okaxis&pn=Merchant', ip: '49.36.xx.xx', location: 'Delhi, IN', createdAt: '2026-08-03T12:20:00Z', downloadCount: 1 },
  { id: 'act-003', type: 'whatsapp', content: 'https://wa.me/919876543210', ip: '117.55.xx.xx', location: 'Bangalore, IN', createdAt: '2026-08-03T12:14:00Z', downloadCount: 3 },
  { id: 'act-004', type: 'url', content: 'https://novamaxstudios.com', ip: '103.21.xx.xx', location: 'Pune, IN', createdAt: '2026-08-03T12:08:00Z', downloadCount: 0 },
  { id: 'act-005', type: 'wifi', content: 'WIFI:T:WPA;S:CafeNet;P:guest123;;', ip: '122.161.xx.xx', location: 'Chennai, IN', createdAt: '2026-08-03T11:55:00Z', downloadCount: 4 },
  { id: 'act-006', type: 'email', content: 'mailto:support@novamaxstudios.com', ip: '27.62.xx.xx', location: 'Hyderabad, IN', createdAt: '2026-08-03T11:40:00Z', downloadCount: 1 },
  { id: 'act-007', type: 'url', content: 'https://instagram.com/novamaxstudios', ip: '49.36.xx.xx', location: 'Kolkata, IN', createdAt: '2026-08-03T11:28:00Z', downloadCount: 2 },
  { id: 'act-008', type: 'upi', content: 'upi://pay?pa=shop@paytm&pn=ShopOwner&am=250', ip: '117.55.xx.xx', location: 'Ahmedabad, IN', createdAt: '2026-08-03T11:15:00Z', downloadCount: 1 },
];

interface AdminDashboardProps {
  onLogout: () => void;
}

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);

  const handleExportCSV = async () => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 800));
    // Backend integration point: GET /api/admin/export/csv
    const headers = ['ID', 'Type', 'Content', 'Created At', 'Downloads'];
    const rows = recentActivity.map(r => [r.id, r.type, `"${r.content}"`, r.createdAt, r.downloadCount].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nova-qr-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully!', { description: `${recentActivity.length} records exported.` });
    setIsExporting(false);
  };

  const handleClearAll = () => {
    // Backend integration point: DELETE /api/admin/history/all
    toast.error('All records cleared — this is a demo action.', { description: 'In production, this would permanently delete all QR history.' });
    setShowDangerZone(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 xl:px-10 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-border bg-card">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-lg gradient-primary flex items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
                    <path d="M6 1L10.5 3.5V6C10.5 9 8 11 6 11.5C4 11 1.5 9 1.5 6V3.5L6 1Z" />
                  </svg>
                </div>
                <span className="badge badge-blue">Admin Access</span>
              </div>
              <h1 className="font-extrabold text-foreground" style={{ fontSize: 'clamp(22px, 6vw, 26px)', letterSpacing: '-0.02em' }}>Admin Panel</h1>
              <p className="text-muted-foreground mt-0.5" style={{ fontSize: '14px' }}>Platform overview · Last updated just now</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleExportCSV}
                disabled={isExporting}
                className="btn-secondary flex items-center gap-2 py-3 px-4 rounded-xl disabled:opacity-60 flex-1 sm:flex-none justify-center"
                style={{ fontSize: '13px', minHeight: '44px' }}
              >
                {isExporting ? (
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.8" strokeDasharray="17" strokeDashoffset="6" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                    <path d="M7 9V2M7 9L5 7M7 9L9 7" />
                    <path d="M1.5 12h11" />
                  </svg>
                )}
                Export CSV
              </button>
              <button
                onClick={onLogout}
                className="btn-ghost flex items-center gap-2 py-3 px-4 rounded-xl text-rose-500 hover:bg-rose-50 flex-1 sm:flex-none justify-center"
                style={{ fontSize: '13px', minHeight: '44px' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M5 2H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2M9.5 10L12 7l-2.5-3M12 7H5" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 xl:px-10 py-5 sm:py-6">
        <div className="max-w-screen-2xl mx-auto space-y-5 sm:space-y-6">

          {/* KPI Cards */}
          <AdminKPIGrid stats={adminStats} />

          {/* Charts + Domains */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 sm:gap-6">
            <div className="xl:col-span-2">
              <AdminQRTypeChart data={qrTypeDistribution} />
            </div>
            <div className="xl:col-span-1">
              <AdminTopDomains domains={topDomains} />
            </div>
          </div>

          {/* Activity Table */}
          <AdminActivityTable records={recentActivity} />

          {/* Danger Zone */}
          <div className="bg-card rounded-3xl border border-rose-200 shadow-card overflow-hidden">
            <button
              onClick={() => setShowDangerZone(!showDangerZone)}
              className="w-full flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-rose-50/50 transition-colors"
              style={{ minHeight: '56px' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="#E11D48" strokeWidth="1.7" strokeLinecap="round">
                    <path d="M7.5 2L13.5 12.5H1.5L7.5 2Z" />
                    <path d="M7.5 6v3M7.5 10.5v.5" />
                  </svg>
                </div>
                <span className="font-semibold text-rose-600" style={{ fontSize: '14px' }}>Danger Zone</span>
              </div>
              <svg
                width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#E11D48" strokeWidth="1.8" strokeLinecap="round"
                style={{ transform: showDangerZone ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', flexShrink: 0 }}
              >
                <path d="M3 5l4 4 4-4" />
              </svg>
            </button>
            {showDangerZone && (
              <div className="px-5 sm:px-6 pb-5 border-t border-rose-100 animate-fade-in">
                <p className="text-muted-foreground mt-4 mb-4" style={{ fontSize: '13px' }}>
                  Destructive actions that cannot be undone. Proceed with extreme caution.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-rose-50 rounded-2xl border border-rose-200">
                  <div>
                    <p className="font-semibold text-rose-700" style={{ fontSize: '14px' }}>Clear All QR History</p>
                    <p className="text-rose-500 mt-0.5" style={{ fontSize: '12px' }}>Permanently deletes all {adminStats.totalQRGenerated.toLocaleString()} QR code records from the database.</p>
                  </div>
                  <button
                    onClick={handleClearAll}
                    className="w-full sm:w-auto flex-shrink-0 px-4 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                    style={{ background: '#E11D48', fontSize: '13px', minHeight: '44px' }}
                  >
                    Clear All Records
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}